import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import DatePicker from '../ui/DatePicker';
import Button from '../ui/Button';
import { listDocumentTypes } from '../../api/workflow';
import { uploadDocument } from '../../api/documents';
import { pushToast } from '../ui/toastBus';

export default function DocumentUploadModal({ open, onClose, candidateId, presetTypeCode, presetStepId, onUploaded }) {
  const queryClient = useQueryClient();
  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes, enabled: open });

  const [file, setFile] = useState(null);
  const [documentTypeId, setDocumentTypeId] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [error, setError] = useState('');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && presetTypeCode && typesQuery.data) {
      const match = typesQuery.data.find((t) => t.code === presetTypeCode);
      if (match) setDocumentTypeId(String(match.id));
    }
  }, [open, presetTypeCode, typesQuery.data]);

  const presetTypeName = typesQuery.data?.find((t) => t.code === presetTypeCode)?.name || presetTypeCode;

  const mutation = useMutation({
    mutationFn: () => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type_id', documentTypeId);
      if (presetStepId) formData.append('candidate_step_id', presetStepId);
      if (expiryDate) formData.append('expiry_date', expiryDate);
      return uploadDocument(candidateId, formData);
    },
    onSuccess: (doc) => {
      pushToast({ type: 'success', message: 'Document uploaded.' });
      queryClient.invalidateQueries({ queryKey: ['candidates', String(candidateId)] });
      handleClose();
      onUploaded?.(doc);
    },
    onError: (err) => setError(err.response?.data?.message || 'Could not upload document.'),
  });

  const handleClose = () => {
    setFile(null);
    setDragging(false);
    setDocumentTypeId('');
    setExpiryDate('');
    setError('');
    onClose();
  };

  // Object URL for an image preview of the chosen file (not for PDF/HEIC, which <img> can't show).
  const [previewUrl, setPreviewUrl] = useState(null);
  useEffect(() => {
    if (!file || !/^image\/(?!heic|heif)/.test(file.type)) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!file || !documentTypeId) {
      setError('Choose a file and document type.');
      return;
    }
    mutation.mutate();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={presetTypeCode ? presetTypeName : 'Upload document'}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={mutation.isPending}>
            Upload
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {!presetTypeCode && (
          <Select label="Document type" value={documentTypeId} onChange={(e) => setDocumentTypeId(e.target.value)}>
            <option value="">Select…</option>
            {(typesQuery.data || [])
              .filter((t) => t.is_active !== false || String(t.id) === documentTypeId)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </Select>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">File (PDF, JPG, PNG, HEIC, WEBP — max 10MB)</label>
          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), inputRef.current?.click())}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pickFile(e.dataTransfer.files?.[0]);
            }}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors ${
              dragging ? 'border-primary bg-primary-50' : 'border-gray-300 hover:border-primary hover:bg-gray-50'
            }`}
          >
            {file ? (
              <>
                {previewUrl && <img src={previewUrl} alt={file.name} className="mb-3 max-h-48 max-w-full rounded-md object-contain" />}
                <p className="text-sm font-medium text-gray-900">{file.name}</p>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                <span className="font-medium text-primary">Drag and drop</span> a file here, or click to browse
              </p>
            )}
            {file && <p className="mt-1 text-xs text-gray-500">Click or drop to replace</p>}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp"
            onChange={(e) => pickFile(e.target.files[0])}
            className="hidden"
          />
        </div>

        <DatePicker label="Expiry date (optional)" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />

        {error && <p className="text-sm text-red-600">{error}</p>}
      </form>
    </Modal>
  );
}
